import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, MapPin, Phone, Mail } from "lucide-react";
import { SITE } from "@/config/site";
import { useDestinations } from "@/lib/content";
import { trackPhoneClick, trackEmailClick } from "@/lib/analytics";

export const Footer = () => {
  const { data: DESTINATIONS, isLoading } = useDestinations();
  if (isLoading) return null;

  return (
    <footer className="bg-ocean-deep text-white/80" data-testid="site-footer">
      <div className="container-x py-20 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <Link to="/" className="font-heading font-bold text-2xl text-white flex items-center gap-2">
            <span className="w-2 h-2 bg-sunset rounded-full" /> Deep Dive Trips
          </Link>
          <p className="mt-4 text-sm leading-relaxed text-white/60">
            An adventure-first travel and expedition company crafting curated and customizable tour
            packages across India.
          </p>
          <div className="flex gap-3 mt-6">
            <a href={SITE.socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Deep Dive Trips on Instagram" data-testid="footer-instagram" className="p-2 border border-white/15 hover:border-sunset hover:text-sunset transition-colors">
              <Instagram size={18} />
            </a>
            <a href={SITE.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Deep Dive Trips on Facebook" data-testid="footer-facebook" className="p-2 border border-white/15 hover:border-sunset hover:text-sunset transition-colors">
              <Facebook size={18} />
            </a>
            <a href={SITE.socials.youtube} target="_blank" rel="noopener noreferrer" aria-label="Deep Dive Trips on YouTube" data-testid="footer-youtube" className="p-2 border border-white/15 hover:border-sunset hover:text-sunset transition-colors">
              <Youtube size={18} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="overline mb-5">Destinations</h4>
          <ul className="space-y-3 text-sm">
            {DESTINATIONS.map((d) => (
              <li key={d.slug}>
                <Link to={d.path} className="hover:text-sunset transition-colors">
                  {d.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="overline mb-5">Company</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/about" className="hover:text-sunset">About</Link></li>
            <li><Link to="/blog" className="hover:text-sunset">Journal</Link></li>
            <li><Link to="/faq" className="hover:text-sunset">FAQ</Link></li>
            <li><Link to="/contact" className="hover:text-sunset">Contact</Link></li>
            <li><Link to="/sitemap" className="hover:text-sunset">Sitemap</Link></li>
            <li><Link to="/privacy" className="hover:text-sunset">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-sunset">Terms &amp; Conditions</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="overline mb-5">Reach Us</h4>
          <ul className="space-y-4 text-sm">
            <li className="flex gap-3 items-start"><MapPin size={16} className="mt-1 shrink-0 text-sunset" /><a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-sunset">{SITE.address}</a></li>
            <li className="flex gap-3 items-start"><Phone size={16} className="mt-1 shrink-0 text-sunset" /><a href={`tel:${SITE.phone.replace(/\s/g, "")}`} onClick={() => trackPhoneClick("footer")} className="hover:text-sunset">{SITE.phone}</a></li>
            <li className="flex gap-3 items-start"><Mail size={16} className="mt-1 shrink-0 text-sunset" /><a href={`mailto:${SITE.enquiryEmail}`} onClick={() => trackEmailClick("footer")} className="hover:text-sunset">{SITE.enquiryEmail}</a></li>
          </ul>
          <p className="text-xs text-white/50 mt-6">{SITE.businessHours}</p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col md:flex-row justify-between text-xs text-white/50 gap-2">
          <span>© {new Date().getFullYear()} Deep Dive Trips. All rights reserved.</span>
          <span>
            Made by{" "}
            <a
              href="https://www.ytwc.in"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-sunset"
            >
              YTWC Pvt. Ltd.
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
};
